<template>
  <div class="highlightingChart" ref="chartdiv"></div>
</template>
<script>
import _ from "lodash";
import moment from "moment";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4lang_th_TH from "@amcharts/amcharts4/lang/th_TH";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { REFINERY_COLORS } from "../../constants/colors";
import { REFINERY_ID, REFINERY_NAME } from "../../constants/index";
import { mapGetters } from "vuex";

export default {
  name: "highlightingChart",
  props: {
    dataLine: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chart: null,
      valueAxis: null,
    };
  },
  watch: {
    dataLine(val) {
      if (val.length > 0) {
        this.highlightingChart();
      }
    },
  },
  mounted() {
    this.highlightingChart();
  },
  computed: {
    ...mapGetters({
      getStationData: "getStationData",
    }),
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    highlightingChart() {
      am4core.useTheme(am4themes_animated);

      const chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);
      const locale = this.$i18n.locale;
      if (locale == "th") {
        chart.language.locale = am4lang_th_TH;
        chart.numberFormatter.language = new am4core.Language();
        chart.numberFormatter.language.locale = am4lang_th_TH;
        chart.dateFormatter.language = new am4core.Language();
        chart.dateFormatter.language.locale = am4lang_th_TH;
      }
      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.renderer.minGridDistance = 70;

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.renderer.inside = false;
      valueAxis.renderer.labels.template.disabled = false;
      valueAxis.min = 0;
      valueAxis.title.text = "ปริมาณน้ำมัน (ลิตร)";

      this.chart = chart;

      const createSeries = (stationId, name, color) => {
        const series = this.chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = stationId;
        series.dataFields.dateX = "date";
        series.name = name;
        series.dataFields.color = color;
        series.stroke = series.fill = am4core.color(color);
        series.strokeWidth = 2;

        var bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.fill = series.fill;

        bullet.events.on("hit", function (event) {
          event.name = name;
          let newDate = event.target.dataItem._dataContext.date;
          vm.$store.dispatch("setTankerName", name);
          vm.$store.dispatch("setType", "tanker");
          vm.$store.dispatch("getStationDataByDate", {
            id: stationId,
            date: moment(newDate).format("DD/MM/YYYY"),
          });
        });

        series.tooltipText = "{name}:{valueY}[/]";
        series.tooltip.pointerOrientation = "right";
        series.tooltip.getFillFromObject = false;
        series.tooltip.background.fill = am4core.color(color);
        series.tooltip.background.cornerRadius = 8;
        series.tooltip.background.fillOpacity = 0.6;
        series.tooltip.label.padding(7, 7, 7, 7);
        series.tooltip.label.fontSize = 12;

        let segment = series.segments.template;
        segment.interactionsEnabled = true;

        let hoverState = segment.states.create("hover");
        hoverState.properties.strokeWidth = 4;

        let dimmed = segment.states.create("dimmed");
        dimmed.properties.stroke = am4core.color("#dadada");

        segment.events.on("over", function (event) {
          processOver(event.target.parent.parent.parent);
        });

        segment.events.on("out", function (event) {
          processOut(event.target.parent.parent.parent);
        });

        segment.events.on("hit", function (event) {
          vm.$store.dispatch(
            "setTankerName",
            event.target.parent.parent.parent.name
          );
          vm.$store.dispatch("setType", "tanker");
        });

        const getVolume = (data) => (id) => {
          let volumes = _.filter(data, { station_id: id }).map((d) => {
            let value = _.get(d, "volumes").map((a) => {
              return Number(a);
            });
            return value;
          });
          let sumVolume = _.reduce(
            volumes,
            (sum, vol) => {
              return sum + _.sum(vol);
            },
            0
          );
          return sumVolume;
        };

        let newData = _.orderBy(this.dataLine, ["departed_at"], ["desc"]);
        const data = _.chain(newData)
          .groupBy((data) => moment(data.departed_at).format("MM/DD/YYYY"))
          .map((data, date) => {
            return {
              date,
              [REFINERY_ID.IRPC]: getVolume(data)(REFINERY_ID.IRPC),
              [REFINERY_ID.SPRC]: getVolume(data)(REFINERY_ID.SPRC),
              [REFINERY_ID.TOP]: getVolume(data)(REFINERY_ID.TOP),
              [REFINERY_ID.BCP]: getVolume(data)(REFINERY_ID.BCP),
              [REFINERY_ID.PTTGC]: getVolume(data)(REFINERY_ID.PTTGC),
              [REFINERY_ID.ESSO]: getVolume(data)(REFINERY_ID.ESSO),
            };
          })
          .value();

        series.data = data;
        return series;
      };

      createSeries(REFINERY_ID.IRPC, REFINERY_NAME.IRPC, REFINERY_COLORS.IRPC);
      createSeries(REFINERY_ID.SPRC, REFINERY_NAME.SPRC, REFINERY_COLORS.SPRC);
      createSeries(REFINERY_ID.TOP, REFINERY_NAME.TOP, REFINERY_COLORS.TOP);
      createSeries(REFINERY_ID.BCP, REFINERY_NAME.BCP, REFINERY_COLORS.BCP);
      createSeries(
        REFINERY_ID.PTTGC,
        REFINERY_NAME.PTTGC,
        REFINERY_COLORS.PTTGC
      );
      createSeries(REFINERY_ID.ESSO, REFINERY_NAME.ESSO, REFINERY_COLORS.ESSO);

      const vm = this;
      chart.legend = new am4charts.Legend();
      chart.legend.position = "";
      chart.legend.scrollable = true;
      chart.legend.itemContainers.template.events.on("over", function (event) {
        processOver(event.target.dataItem.dataContext);
      });

      chart.legend.itemContainers.template.events.on("out", function (event) {
        processOut(event.target.dataItem.dataContext);
      });

      chart.legend.itemContainers.template.events.on("hit", function (event) {
        const name = event.target.dataItem.dataContext.name;
        vm.$store.dispatch("setTankerName", name);
        vm.$store.dispatch("setType", "tanker");
      });

      // Add cursor
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.behavior = "zoomX";
      chart.cursor.xAxis = dateAxis;

      const processOver = (hoveredSeries) => {
        hoveredSeries.toFront();

        hoveredSeries.segments.each((segment) => {
          segment.setState("hover");
        });

        chart.series.each((series) => {
          if (series != hoveredSeries) {
            series.segments.each((segment) => {
              segment.setState("dimmed");
            });
            series.bulletsContainer.setState("dimmed");
          }
        });
      };

      const processOut = () => {
        chart.series.each(function (series) {
          series.segments.each(function (segment) {
            segment.setState("default");
          });
          series.bulletsContainer.setState("default");
        });
      };
      this.chart = chart;
      this.valueAxis = valueAxis;
    },
  },
};
</script>

<style scoped>
.highlightingChart {
  width: 100%;
  height: 400px;
  max-width: 100%;
}
</style>
